import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import splitPinned from "components/Screens/Marketplace/Customer/TalentNetwork/pinnedOptions";

export interface FilterSheetSection {
  key: string;
  label: string;
  /** A line at the top of the open section saying what the filter is for. */
  helper?: ReactNode;
  options: { value: string; label: string; description?: string; group?: string }[];
  /** A line under the section (the role section's Request a hire prompt). */
  note?: ReactNode;
  /** One pick only (Role): plain rows, no box or circle; picking the chosen one again clears it. */
  single?: boolean;
  /** Option values listed first, above a divider, with whatever is ticked as the sheet opens (English, Spanish, the onboarding apps). */
  pinned?: string[];
}

interface Props {
  sections: FilterSheetSection[];
  selected: Record<string, string[]>;
  // eslint-disable-next-line no-unused-vars
  onApply: (next: Record<string, string[]>) => void;
  onClose: () => void;
}

/**
 * Phones: every filter on one full-screen sheet (prototype .fsheet). Each filter is a section that opens on a tap,
 * all closed to start; a closed one lists its picks, each with an x to take it off, and any with picks has its
 * own Clear. Picks apply on "Show profiles".
 */
function MobileFilterSheet({ sections, selected, onApply, onClose }: Props) {
  const [draft, setDraft] = useState<Record<string, string[]>>(selected);
  const [open, setOpen] = useState<string[]>([]);
  // What was ticked as the sheet opened goes on top, so nothing moves under the finger while picking.
  const [opening] = useState(selected);

  const picks = (key: string) => draft[key] || [];
  const setPicks = (key: string, values: string[]) => setDraft((d) => ({ ...d, [key]: values }));
  const toggle = (key: string, value: string, single?: boolean) => {
    const cur = picks(key);
    if (single) setPicks(key, cur.includes(value) ? [] : [value]);
    else setPicks(key, cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
  };
  const expand = (key: string) => setOpen((o) => (o.includes(key) ? o.filter((k) => k !== key) : [...o, key]));

  return (
    <Dialog open fullScreen onClose={onClose} aria-labelledby="tn-fsheet-title" slotProps={{ paper: { className: "tn-fsheet" } }}>
      <div className="tn-fsheet__head">
        <h2 id="tn-fsheet-title">Filters</h2>
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className="tn-fsheet__body">
        {sections.map((section) => {
          const chosen = picks(section.key);
          const isOpen = open.includes(section.key);
          const labelOf = (v: string) => section.options.find((o) => o.value === v)?.label || v;
          const split = section.single ? null : splitPinned(section.options, [...(section.pinned || []), ...(opening[section.key] || [])]);
          const list = split ? [...split.top, ...split.rest] : section.options;
          return (
            <section key={section.key} className={`tn-fsheet__sec${isOpen ? " is-open" : ""}`}>
              <div className="tn-fsheet__sec-head">
                <button
                  type="button"
                  className="tn-fsheet__sec-toggle"
                  aria-expanded={isOpen}
                  aria-controls={`tn-fsheet-${section.key}`}
                  onClick={() => expand(section.key)}
                >
                  <span className="tn-fsheet__sec-label">{section.label}</span>
                  {/* A count only where several can be picked; Role is one pick, its chip says which */}
                  {!section.single && chosen.length > 0 && <span className="tn-fsheet__sec-count">{chosen.length}</span>}
                  <ExpandMoreIcon className="tn-fsheet__sec-chevron" />
                </button>
                {chosen.length > 0 && (
                  <button type="button" className="tn-fsheet__sec-clear" onClick={() => setPicks(section.key, [])}>
                    Clear
                  </button>
                )}
              </div>

              {!isOpen && chosen.length > 0 && (
                <div className="tn-fsheet__chips">
                  {chosen.map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="tn-fsheet__chip"
                      aria-label={`Remove ${labelOf(v)}`}
                      onClick={() => toggle(section.key, v, section.single)}
                    >
                      {labelOf(v)}
                      <CloseIcon />
                    </button>
                  ))}
                </div>
              )}

              {isOpen && (
                <div id={`tn-fsheet-${section.key}`} className="tn-fsheet__opts">
                  {section.helper && <p className="tn-fsheet__helper">{section.helper}</p>}
                  {list.map((opt, i) => (
                    <Fragment key={opt.value}>
                      {i > 0 && i === split?.top.length && <div className="tn-fsheet__divider" />}
                      {/* A heading where the group changes (Role: Medical, Dental, ...). */}
                      {opt.group && opt.group !== list[i - 1]?.group && <div className="tn-fsheet__group">{opt.group}</div>}
                      {section.single ? (
                        <button
                          type="button"
                          className={`tn-fsheet__opt tn-fsheet__opt--single${chosen.includes(opt.value) ? " tn-fsheet__opt--on" : ""}`}
                          aria-pressed={chosen.includes(opt.value)}
                          onClick={() => toggle(section.key, opt.value, true)}
                        >
                          <span className="tn-fsheet__opt-text">
                            <span className="tn-fsheet__opt-label">{opt.label}</span>
                            {opt.description && <span className="tn-fsheet__opt-desc">{opt.description}</span>}
                          </span>
                        </button>
                      ) : (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                        <label className="tn-fsheet__opt">
                          <input type="checkbox" checked={chosen.includes(opt.value)} onChange={() => toggle(section.key, opt.value)} />
                          <span className="tn-fsheet__opt-text">
                            <span className="tn-fsheet__opt-label">{opt.label}</span>
                            {opt.description && <span className="tn-fsheet__opt-desc">{opt.description}</span>}
                          </span>
                        </label>
                      )}
                    </Fragment>
                  ))}
                  {section.note && <div className="tn-fsheet__note">{section.note}</div>}
                </div>
              )}
            </section>
          );
        })}
      </div>
      <div className="tn-fsheet__foot">
        <button type="button" className="tn-fsheet__clear" onClick={() => setDraft({})}>
          Clear
        </button>
        <button
          type="button"
          className="tn-fsheet__apply"
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          Show profiles
        </button>
      </div>
    </Dialog>
  );
}

export default MobileFilterSheet;
